import {
  Component,
  computed,
  DestroyRef,
  effect,
  Inject,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  FeatureService,
  GlobalStore,
  SessionStorageService,
} from '@maximizer/core/shared/data-access';
import { OAuth2Service } from '@maximizer/oauth2/data-access';
import { CDN_URL } from '@maximizer/core/shared/domain';
import { InsightsService } from '@maximizer/core/shared/insights';
import { DisposableComponent } from '@maximizer/core/shared/ui/components/base';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import {
  EmailSyncService,
  HeartbeatEntity,
  HeartbeatPayload,
  UserSyncConfiguration,
} from '@maximizer/outlook-sync/data-access';
import { EmailDetailComponent } from '@maximizer/outlook/interaction-log/ui';
import { ComposeService } from '@maximizer/outlook/main/data-access';
import {
  AbEntryService,
  ComposeStore,
  EmailService,
  nullIfBlank,
  OutlookService,
  OutlookStore,
  TrackerService,
  tryOfficeAsync,
} from '@maximizer/outlook/shared/data-access';
import {
  AbEntryLastContactDatePreference,
  AbEntryType,
  EmailEntryType,
  EmailPosition,
  EntryType,
  EventAction,
  EventJourney,
  EventMetadata,
  EventModule,
  OutlookEmail,
  OutlookEmailCreationResult,
  OutlookEmailDetail,
} from '@maximizer/outlook/shared/domain';
import {
  OUTLOOK_SHARED_UI,
  OutlookNotificationComponent,
} from '@maximizer/outlook/shared/ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_SWITCH } from '@progress/kendo-angular-inputs';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { KENDO_TOOLTIP } from '@progress/kendo-angular-tooltip';
import { debounceTime, firstValueFrom, skip, Subject } from 'rxjs';
import {
  ComposeEntryGroup,
  EntryMovePositionEvent,
  EntrySelectionEvent,
} from '../compose.model';
import { OutlookEntryListItemComponent } from '../entry-list-item/entry-list-item.component';

@Component({
  selector: 'maximizer-outlook-compose-save-to-email',
  templateUrl: './save-to-email.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    EmailDetailComponent,
    FormsModule,
    KENDO_BUTTON,
    KENDO_LABEL,
    KENDO_SWITCH,
    KENDO_TOOLTIP,
    OutlookEntryListItemComponent,
    OUTLOOK_SHARED_UI,
    TranslatePipe,
    UI_KENDO,
  ],
})
export class OutlookComposeSaveToEmailComponent
  extends DisposableComponent
  implements OnInit, OnDestroy
{
  notification =
    viewChild.required<OutlookNotificationComponent>('notification');

  private readonly destroyRef = inject(DestroyRef);
  private readonly heartbeatDebounce$ = new Subject<void>();
  // Wait for rapid selection/init changes to settle before syncing a heartbeat.
  private readonly heartbeatDebounceMs = 1000;

  private readonly storageName_saveToSwitch = 'saveToSwitch';
  private readonly storageName_emailsToSave = 'emailsToSaveInMaximizer';
  private readonly storageName_heartbeatId = 'heartbeatId';

  loading = {
    skeleton: false,
    sync: false,
  };

  EntryType = EntryType;
  outlookEmails: OutlookEmail[] = [];
  emailsNotInMaximizer: OutlookEmail[] = [];
  saveToEmailSwitch = false;
  internetMessageId: string | null = null;
  private activeHeartbeatId: string | null = null;
  private deleteHeartbeatPromise: Promise<void> | null = null;
  private recoveringProperties = false;
  isEmailSyncFeatureFlagEnabled = false;
  configurationId: string | undefined = undefined;
  isFirstLoad = true;
  userRightsToSaveEmail = false;
  saveEmailDetail = false;
  saveEmailDetailProperties?: OutlookEmailDetail;
  trackingEventLocation = EventModule.Compose;
  parentItems = ['to', 'cc', 'bcc', 'saveOnly'];

  conversationIdChecked = signal(false);

  private readonly baseSaveForEntryList = signal<ComposeEntryGroup[]>([
    {
      id: 1,
      localId: 'to',
      translation: 'outlook.compose.to',
      displayName: '',
      items: [],
    },
    {
      id: 2,
      localId: 'cc',
      translation: 'outlook.compose.cc',
      displayName: '',
      items: [],
    },
    {
      id: 3,
      localId: 'bcc',
      translation: 'outlook.compose.bcc',
      displayName: '',
      items: [],
    },
    {
      id: 4,
      localId: 'saveOnly',
      translation: 'outlook.compose.saveOnly',
      displayName: '',
      items: [],
    },
  ]);

  // Computed signal properties
  isOutlookLegacyDesktop = computed(() => {
    return this.outlookService.isOutlookLegacyDesktop();
  });

  autoSaveDisabled = computed(() => {
    const syncConfig = this.outlookStore.syncConfiguration();
    return !syncConfig?.enabled;
  });

  hasSyncErrors = computed(() => {
    return this.outlookStore.hasSyncErrors();
  });

  showSaveOnSend = computed(() => {
    if (
      this.outlookStore.preferences().saveNow ||
      !this.outlookStore.versionValidForNewSync()
    )
      return false;

    let syncEnabled = this.outlookStore.syncConfiguration()?.enabled ?? false;

    return (
      syncEnabled &&
      this.isEmailSyncFeatureFlagEnabled &&
      !!this.outlookStore.isCloud() &&
      this.userRightsToSaveEmail &&
      !this.isOutlookLegacyDesktop() &&
      !this.outlookStore.isSharedMailbox()
    );
  });

  showSaveNow = computed(() => {
    if (!this.userRightsToSaveEmail) {
      return false;
    }
    if (this.outlookStore.preferences().saveNow) {
      return true;
    }

    return (
      !this.isEmailSyncFeatureFlagEnabled ||
      !this.outlookStore.syncConfiguration()?.enabled ||
      !(this.outlookStore.isCloud() ?? false) ||
      !this.outlookStore.versionValidForNewSync() ||
      this.isOutlookLegacyDesktop() ||
      this.outlookStore.isSharedMailbox()
    );
  });

  noEmailsSelected = computed(() => {
    const allItems = this.saveForEntryList().flatMap((group) => group.items);
    return !allItems.some((item) => item.isSelected);
  });

  showSaveOnSendWarning = computed(() => {
    return (
      !!this.outlookStore.isCloud() &&
      this.userRightsToSaveEmail &&
      (this.isOutlookLegacyDesktop() ||
        this.outlookStore.isSharedMailbox() ||
        !this.outlookStore.versionValidForNewSync())
    );
  });

  saveForEntryList = computed(() => {
    const baseList = [...this.baseSaveForEntryList()];
    const saveOnlySection = baseList.find(
      (section) => section.localId === 'saveOnly',
    );

    if (saveOnlySection && this.conversationIdChecked()) {
      const storeEntries = this.composeStore.saveOnlyEntries();
      saveOnlySection.items = storeEntries.map((entry) => {
        // Preserve existing isSelected state, or default to true for new entries
        const existingItem = saveOnlySection.items.find(
          (item) => item.localId === entry.localId,
        );
        return {
          ...entry,
          isSelected: existingItem ? existingItem.isSelected : true,
        };
      });
    }

    return baseList;
  });

  constructor(
    @Inject(CDN_URL) public readonly cdn: string,
    private readonly translate: TranslateService,
    private readonly storage: SessionStorageService,
    private readonly outlookService: OutlookService,
    private readonly oauth2Service: OAuth2Service,
    private readonly insightsService: InsightsService,
    private readonly outlookSyncService: EmailSyncService,
    private readonly emailService: EmailService,
    private readonly tracker: TrackerService,
    private readonly abEntryService: AbEntryService,
    private readonly router: Router,
    private readonly composeService: ComposeService,
    public globalStore: GlobalStore,
    public outlookStore: OutlookStore,
    public feature: FeatureService,
    public composeStore: ComposeStore,
  ) {
    super();

    this.isEmailSyncFeatureFlagEnabled = this.feature.isFeatureOn(
      'microsoft-outlook-sync-emails-shown',
      false,
    );

    effect(() => {
      if (this.hasSyncErrors()) {
        this.saveToEmailSwitch = false;
        this.heartbeatDebounce$.next();
      }
    });

    effect(() => {
      if (
        this.outlookStore.hasActiveAccount() &&
        this.outlookStore.hasInvalidComposeProperties()
      ) {
        void this.recoverComposeProperties();
      }
    });
  }

  ngOnInit(): void {
    this.loading.sync = true;

    this.heartbeatDebounce$
      .pipe(
        debounceTime(this.heartbeatDebounceMs),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        void this.sendHeartbeat();
      });

    this.outlookService.emailChanges$
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.isFirstLoad) return;
        this.heartbeatDebounce$.next();
      });

    this.outlookService.composeSubjectChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.heartbeatDebounce$.next();
      });

    this.outlookStore.loadUsers();

    this.validateConversationId();

    for (const entry of this.baseSaveForEntryList()) {
      entry.displayName = this.translate.instant(entry.translation);
    }

    this.baseSaveForEntryList.set([...this.baseSaveForEntryList()]);

    if (!this.oauth2Service.mxUserId) {
      this.stopLoading();
      return;
    }

    const userRights = this.globalStore.session()?.user.rights;
    this.userRightsToSaveEmail = userRights?.documents.insert ?? false;
    if (!this.userRightsToSaveEmail) {
      this.stopLoading();
      return;
    }

    if (!this.showSaveOnSend()) {
      this.stopLoading();
      return;
    }

    const outlookSync = this.outlookStore.syncConfiguration();
    if (!outlookSync) {
      this.stopLoading();
      this.resetComponentProperties();
      return;
    }

    this.configurationId = outlookSync.id;
    void this.outlookService.startComposeSubjectChangeTracking();

    this.populateComposeProperties(outlookSync)
      .then(async () => {
        await this.restoreHeartbeatIdFromSession();
        const switchInSession = await this.getSwitchInSession();
        this.saveToEmailSwitch =
          !this.hasSyncErrors() && (switchInSession ?? true);
        if (this.saveToEmailSwitch) {
          this.heartbeatDebounce$.next();
        }
        this.stopLoading();
      })
      .catch(async (error) => {
        this.insightsService.trackException(error, {
          message: 'Failed to populate onSend properties',
          outlookSync,
        });
        await this.setInvalidProperties();
        this.stopLoading();
      });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.outlookService.stopComposeSubjectChangeTracking();
  }

  private stopLoading() {
    this.loading.sync = false;
    this.loading.skeleton = false;
  }

  private validateConversationId(): void {
    const item = this.outlookService.mailboxItem;
    if (!item) return;

    const lastConversationId = this.storage.get('lastConversationId');
    const currentConversationId = item.conversationId;

    if (lastConversationId && lastConversationId !== currentConversationId) {
      this.storage.remove('saveOnlyEntries');
      this.composeStore.updateSaveOnlyEntries([]);
    } else {
      this.composeStore.initializeFromSessionStorage();
    }

    this.storage.set('lastConversationId', currentConversationId);
    this.conversationIdChecked.set(true);
  }

  // Called every time there's a change in the email addresses in Outlook
  async initialize(maximizerEntries: OutlookEmail[]): Promise<void> {
    try {
      this.loading.skeleton = true;
      this.outlookEmails = this.composeStore.inMaximizer();
      this.sortEmailsWithOutlookOrder(maximizerEntries);

      const resultWithLocalId = this.ensureLocalIdsForEntries(maximizerEntries);

      this.populateSaveToEntryList(resultWithLocalId);
      if (this.isFirstLoad) {
        this.isFirstLoad = false;
        return await this.handleFirstLoad();
      }
    } catch (error) {
      this.insightsService.trackException({ error: error as Error });
      this.composeStore.updateSelectedEmailsToSave([]);
      await this.checkFirstSectionItems_To();
    } finally {
      this.loading.skeleton = false;
    }
  }

  updateEmailsToSaveInMaximizer(
    email: OutlookEmail,
    action: 'add' | 'remove',
  ): void {
    if (
      this.showSaveNow() ||
      (this.showSaveOnSend() && !this.autoSaveDisabled())
    ) {
      if (action === 'remove') {
        this.composeStore.removeEmailFromSelected(email);
      } else {
        this.composeStore.addEmailToSelected(email);
      }
    }
  }

  async onEntryPositionChange(event: EntryMovePositionEvent): Promise<void> {
    const { entry, oldPosition, newPosition } = event;
    let success = false;
    this.saveCheckboxStatesToSession();

    try {
      if (newPosition === 'remove') {
        await this.handleEntryRemoval(entry, oldPosition);
        success = true;
        return;
      }

      if (newPosition === 'saveOnly') {
        await this.handleMoveToSaveOnly(entry, oldPosition);
        success = true;
        return;
      }

      // Handle moves from saveOnly to email positions (to/cc/bcc)
      if (oldPosition === 'saveOnly') {
        await this.handleMoveFromSaveOnly(entry, newPosition);
        success = true;
        return;
      }

      // Handle moves between email positions (to/cc/bcc)
      await this.handleMovesBetweenEmailPositions(
        entry,
        oldPosition,
        newPosition,
      );
      success = true;
    } catch (error) {
      success = false;
      this.notification().show('add', 'error');
      this.insightsService.trackException(
        { error: error as Error },
        {
          message: 'Failed to move entry position',
          oldPosition,
          newPosition,
        },
      );
    } finally {
      this.tracker.trackEvent({
        module: EventModule.Compose,
        journey: EventJourney.MoveRecipient,
        action: EventAction.Completed,
        metadata: {
          direction: EventMetadata.Outgoing,
          recipient: newPosition,
          result: success ? 'success' : 'failed',
        },
      });
    }
  }

  private async handleEntryRemoval(
    entry: OutlookEmail,
    oldPosition: EmailPosition,
  ): Promise<void> {
    if (oldPosition === 'saveOnly') {
      // Save-only entries also live in inMaximizer (they feed Related keys);
      // remove from all three so the entry stops contributing anywhere.
      this.composeStore.removeEmailFromSaveOnly(entry);
      this.composeStore.removeEmailFromInMaximizer(entry);
      this.composeStore.removeEmailFromSelected(entry);
      this.removeCheckboxStateForEmail(entry);
    } else {
      // removeEmailFromPosition cleans up selection/local state after Outlook confirms.
      await this.removeEmailFromPosition(entry, oldPosition);
    }
  }

  private async handleMoveToSaveOnly(
    entry: OutlookEmail,
    oldPosition: EmailPosition,
  ): Promise<void> {
    let success = false;

    try {
      await this.removeEmailFromPosition(entry, oldPosition);
      this.addEntryToSaveOnly(entry);

      success = true;
    } finally {
      this.tracker.trackEvent({
        module: EventModule.Compose,
        journey: EventJourney.MoveRecipient,
        action: EventAction.Started,
        metadata: {
          direction: EventMetadata.Outgoing,
          recipient: 'saveOnly',
          result: success ? 'success' : 'failed',
        },
      });
    }
  }

  private async handleMoveFromSaveOnly(
    entry: OutlookEmail,
    newPosition: EmailPosition,
  ): Promise<void> {
    await this.outlookService.addEmailToPosition(entry, newPosition);

    this.composeStore.removeEmailFromSaveOnly(entry);
    this.composeStore.removeEmailFromSelected(entry);
    this.removeCheckboxStateForEmail(entry);
    this.updateEmailsToSaveInMaximizer(entry, 'add');
  }

  private async handleMovesBetweenEmailPositions(
    entry: OutlookEmail,
    oldPosition: EmailPosition,
    newPosition: EmailPosition,
  ): Promise<void> {
    // Standard move between email positions (to, cc, bcc)
    await this.moveEntryEmailPosition(entry, oldPosition, newPosition);
    this.updateEmailsToSaveInMaximizer(entry, 'add');
  }

  addEntryToSaveOnly(email: OutlookEmail): void {
    const emailForSaveOnly = { ...email };
    emailForSaveOnly.position = 'saveOnly';
    emailForSaveOnly.localId = this.composeService.generateLocalId(
      email,
      'saveOnly',
    );

    this.composeStore.addEmailToSaveOnly(emailForSaveOnly);
  }

  addEntryToPosition(email: OutlookEmail): void {
    const position = email.position;
    if (position !== 'to' && position !== 'cc' && position !== 'bcc') {
      return;
    }

    const currentList = [...this.baseSaveForEntryList()];
    const targetSection = currentList.find(
      (section) => section.localId === position,
    );
    if (!targetSection) {
      return;
    }

    const alreadyInSection = targetSection.items.some(
      (item) =>
        item.id === email.id && item.emailAddress === email.emailAddress,
    );
    if (alreadyInSection) {
      return;
    }

    const sameEmailCount = targetSection.items.filter(
      (item) => item.emailAddress === email.emailAddress,
    ).length;
    const localId = this.composeService.generateLocalId(
      email,
      position,
      sameEmailCount,
    );

    const toCount =
      currentList.find((section) => section.localId === 'to')?.items.length ??
      0;
    const ccCount =
      currentList.find((section) => section.localId === 'cc')?.items.length ??
      0;
    const isPrimarySection =
      position === 'to' ||
      (position === 'cc' && toCount === 0) ||
      (position === 'bcc' && toCount === 0 && ccCount === 0);

    targetSection.items = [
      ...targetSection.items,
      { ...email, localId, isSelected: isPrimarySection },
    ];

    this.baseSaveForEntryList.set(currentList);
    this.handleSaveForEntryListChange();
    this.saveCheckboxStatesToSession();
  }

  private getPrimaryRecipientSection(
    to: OutlookEmail[],
    cc: OutlookEmail[],
    bcc: OutlookEmail[],
  ): EmailPosition {
    if (to.length > 0) return 'to';
    if (cc.length > 0) return 'cc';
    if (bcc.length > 0) return 'bcc';
    return 'to';
  }

  onEntrySelectionChange(event: EntrySelectionEvent): void {
    const currentList = [...this.baseSaveForEntryList()];
    let updated = false;

    for (const group of currentList) {
      const item = group.items.find(
        (item) => item.localId === event.entry.localId,
      );
      if (item) {
        item.isSelected = event.selected;
        updated = true;
      }
    }

    if (updated) {
      this.baseSaveForEntryList.set(currentList);
      this.handleSaveForEntryListChange();
      this.saveCheckboxStatesToSession();
    }
  }

  openEntryDetail(entry: OutlookEmail): void {
    this.router.navigate([`/${entry.type}/${entry.id}`]);
  }

  async removeEmailFromPosition(
    email: OutlookEmail,
    originalPosition: EmailPosition,
  ): Promise<void> {
    this.saveCheckboxStatesToSession();

    if (
      originalPosition === 'to' ||
      originalPosition === 'cc' ||
      originalPosition === 'bcc'
    ) {
      await this.outlookService.removeEmailFromPosition(
        email,
        originalPosition,
      );
      this.composeStore.removeEmailFromSelected(email);
      this.removeEmailFromLocalList(email, originalPosition);
    } else if (originalPosition === 'saveOnly') {
      this.composeStore.removeEmailFromSaveOnly(email);
    }

    this.removeCheckboxStateForEmail(email);
    this.updateEmailsToSaveInMaximizer(email, 'remove');
  }

  private removeEmailFromLocalList(
    email: OutlookEmail,
    position: EmailPosition,
  ): void {
    const currentList = [...this.baseSaveForEntryList()];

    // Find the section by localId instead of using magic array indices
    const targetSection = currentList.find(
      (section) => section.localId === position,
    );

    if (targetSection) {
      targetSection.items = targetSection.items.filter(
        (item) => item.localId !== email.localId,
      );
      this.baseSaveForEntryList.set(currentList);
    }
  }

  async moveEntryEmailPosition(
    email: OutlookEmail,
    oldPosition: EmailPosition,
    newPosition: EmailPosition,
  ): Promise<void> {
    await this.outlookService.moveEmailToPosition(
      email,
      oldPosition,
      newPosition,
    );
    email.position = newPosition;
  }

  resetComponentProperties(): void {
    this.outlookService
      .getComposeEmailId()
      .then((mailboxItemId) => {
        this.storage.remove(
          this.storageName_emailsToSave + ':' + mailboxItemId,
        );
        this.storage.remove(
          this.storageName_saveToSwitch + ':' + mailboxItemId,
        );
        this.storage.remove(this.storageName_heartbeatId + ':' + mailboxItemId);
      })
      .catch(() => void 0);

    this.saveToEmailSwitch = false;
    void this.deleteHeartbeatIfActive();
    this.outlookService.stopHeartbeat();

    const currentList = this.baseSaveForEntryList();
    currentList.forEach((o) => (o.items = []));
    this.baseSaveForEntryList.set([...currentList]);

    this.composeStore.updateSelectedEmailsToSave([]);
    this.composeStore.updateSaveOnlyEntries([]);
    this.composeStore.setHeartbeatActive(false);
    this.outlookEmails = [];
    this.clearAllCheckboxStates();
  }

  private async setInvalidProperties(): Promise<void> {
    this.outlookStore.setHasInvalidComposeProperties(true);
    await this.removeStorageItems();
    this.resetComponentProperties();
    this.saveToEmailSwitch = false;
  }

  async removeStorageItems(): Promise<void> {
    const composeId =
      this.composeStore.composeId() ?? this.outlookService.mailboxItem?.itemId;
    this.composeStore.setComposeId(composeId ?? null);

    if (!composeId) {
      this.clearAllCheckboxStates();
      await this.deleteHeartbeatIfActive();
      this.outlookService.stopHeartbeat();
      return;
    }

    this.storage.remove(this.storageName_emailsToSave + ':' + composeId);
    this.storage.remove(this.storageName_saveToSwitch + ':' + composeId);
    this.clearAllCheckboxStates();
    await this.deleteHeartbeatIfActive();
    this.outlookService.stopHeartbeat();
  }

  private async populateComposeProperties(
    syncConfiguration: UserSyncConfiguration,
  ): Promise<void> {
    const composeId = await this.outlookService.getComposeEmailId();
    this.composeStore.setComposeId(composeId);

    if (!composeId) {
      this.insightsService.trackException(
        { error: new Error('ComposeId is null') },
        { mailbox: this.outlookService.mailbox },
      );
      await this.setInvalidProperties();
      return;
    }

    this.internetMessageId =
      await this.outlookService.getInternetMessageId(composeId);

    if (!this.internetMessageId) {
      this.insightsService.trackException(
        {
          error: new Error('internetMessageId is null, heartbeat disabled'),
        },
        {
          composeId,
          mailbox: this.outlookService.mailbox,
        },
      );
      await this.setInvalidProperties();
      return;
    }

    if (!this.showSaveOnSend()) return;

    this.outlookStore.setHasInvalidComposeProperties(false);
    this.configurationId = syncConfiguration.id;
    this.startHeartbeatTimer();
  }

  private async recoverComposeProperties(): Promise<void> {
    if (this.recoveringProperties || !this.showSaveOnSend()) return;
    const syncConfiguration = this.outlookStore.syncConfiguration();
    if (!syncConfiguration?.enabled) return;

    this.recoveringProperties = true;
    try {
      await this.populateComposeProperties(syncConfiguration);
      if (!this.outlookStore.hasInvalidComposeProperties()) {
        const switchInSession = await this.getSwitchInSession();
        this.saveToEmailSwitch =
          !this.hasSyncErrors() && (switchInSession ?? true);
        this.heartbeatDebounce$.next();
      }
    } finally {
      this.recoveringProperties = false;
    }
  }

  private async buildHeartbeatPayload(): Promise<HeartbeatPayload | null> {
    const emailId = this.composeStore.composeId();
    if (!emailId || !this.internetMessageId || !this.configurationId) {
      return null;
    }

    const subject = await this.outlookService.getSubject();
    const graphMessageId = this.outlookService.getGraphEmailId(emailId);

    const sections = this.saveForEntryList();
    const allItems = sections.flatMap((group) => group.items);
    const selectedEntries = allItems.filter(
      (item) => item.isSelected && item.id,
    );

    const recipientSections = sections.filter(
      (section) => section.localId !== 'saveOnly',
    );
    const toRecipients = recipientSections
      .flatMap((section) => section.items)
      .filter((item) => item.emailAddress)
      .map((item) => item.emailAddress);

    const uniqueEntriesMap = new Map<string, HeartbeatEntity>();
    for (const entry of selectedEntries) {
      if (entry.id && !uniqueEntriesMap.has(entry.id)) {
        uniqueEntriesMap.set(entry.id, {
          maximizerKey: entry.id,
          maximizerEntityType: entry.type === 'lead' ? 'Lead' : 'AbEntry',
        });
      }
    }

    const entities = Array.from(uniqueEntriesMap.values());
    if (entities.length === 0) return null;

    if (entities.length > 50) {
      this.insightsService.trackException(
        {
          error: new Error(
            `Heartbeat entities exceed limit: ${entities.length}`,
          ),
        },
        { emailId, entitiesCount: entities.length },
      );
      return null;
    }

    return {
      emailId,
      internetMessageId: this.internetMessageId,
      graphMessageId,
      subject,
      to: [...new Set(toRecipients)],
      entities,
    };
  }

  private async sendHeartbeat(): Promise<void> {
    if (!this.canSendHeartbeat()) {
      await this.deleteHeartbeatIfActive();
      return;
    }

    const payload = await this.buildHeartbeatPayload();
    if (!payload) {
      await this.deleteHeartbeatIfActive();
      return;
    }

    try {
      const heartbeatId = await firstValueFrom(
        this.outlookSyncService.upsertHeartbeat(this.configurationId!, payload),
      );
      if (heartbeatId) {
        this.activeHeartbeatId = heartbeatId;
        await this.saveHeartbeatIdInSession(heartbeatId);
        this.composeStore.setHeartbeatActive(true);
      }
    } catch {
      // Best-effort: the periodic heartbeat will try again later.
    }
  }

  private deleteHeartbeatIfActive(): Promise<void> {
    this.deleteHeartbeatPromise ??= this.deleteHeartbeat().finally(() => {
      this.deleteHeartbeatPromise = null;
    });
    return this.deleteHeartbeatPromise;
  }

  private async deleteHeartbeat(): Promise<void> {
    if (!this.activeHeartbeatId || !this.configurationId) return;

    let deleted = false;
    try {
      deleted = await firstValueFrom(
        this.outlookSyncService.deleteHeartbeat(
          this.configurationId,
          this.activeHeartbeatId,
        ),
      );
    } catch {
      // Best-effort: leave activeHeartbeatId set so a later call can retry.
    }

    if (deleted) {
      this.activeHeartbeatId = null;
      await this.saveHeartbeatIdInSession(null);
      this.composeStore.setHeartbeatActive(false);
    }
  }

  private async saveHeartbeatIdInSession(
    heartbeatId: string | null,
  ): Promise<void> {
    try {
      const mailboxItemId = await this.outlookService.getComposeEmailId();
      if (!mailboxItemId) return;
      const key = `${this.storageName_heartbeatId}:${mailboxItemId}`;
      if (heartbeatId) {
        this.storage.set(key, heartbeatId);
      } else {
        this.storage.remove(key);
      }
    } catch (error) {
      this.insightsService.trackException(
        { error: error as Error },
        { message: 'Failed to persist heartbeat id' },
      );
    }
  }

  private async restoreHeartbeatIdFromSession(): Promise<void> {
    try {
      const mailboxItemId = await this.outlookService.getComposeEmailId();
      if (!mailboxItemId) return;
      const stored = this.storage.get(
        `${this.storageName_heartbeatId}:${mailboxItemId}`,
      );
      if (stored) this.activeHeartbeatId = stored;
    } catch {
      // non-fatal
    }
  }

  private canSendHeartbeat(): boolean {
    return (
      this.showSaveOnSend() &&
      !this.autoSaveDisabled() &&
      this.saveToEmailSwitch &&
      !this.outlookStore.isSharedMailbox() &&
      !!this.internetMessageId &&
      !!this.configurationId
    );
  }

  private startHeartbeatTimer(): void {
    void this.outlookService.startHeartBeat(
      async () => {
        await this.sendHeartbeat();
      },
      () => {
        void this.deleteHeartbeatIfActive();
      },
    );
  }

  private async handleFirstLoad(): Promise<void> {
    const exists = await this.checkIfMultiselectExistsInStorage();
    const savedCheckboxStates = this.composeStore.getCheckboxStateFromSession();

    // If saved checkbox states, don't override with default states
    if (savedCheckboxStates && Object.keys(savedCheckboxStates).length > 0) {
      return;
    }

    if (!exists) {
      await this.checkFirstSectionItems_To();
    } else {
      const children = this.saveForEntryList()
        .flatMap((o) => o.items)
        .filter((o) => exists.includes(o.id ?? ''));

      this.handleSaveForEntryListChange(children);
    }
  }

  private async checkFirstSectionItems_To(): Promise<void> {
    const currentList = this.saveForEntryList();
    const enableFirstTime: OutlookEmail[] = [
      ...currentList[0].items.filter((o) => o.id),
    ];
    await this.handleSaveForEntryListChange(enableFirstTime);
  }

  private sortEmailsWithOutlookOrder(array: OutlookEmail[]): OutlookEmail[] {
    // Handle entries with same email
    const map = this.outlookEmails.map(
      (o) => `${o.id}_${o.emailAddress}_${o.position}`,
    );
    return array.sort((a, b) => {
      const aKey = `${a.id}_${a.emailAddress}_${a.position}`;
      const bKey = `${b.id}_${b.emailAddress}_${b.position}`;
      return map.indexOf(aKey) - map.indexOf(bKey);
    });
  }

  private ensureLocalIdsForEntries(
    maximizerEntries: OutlookEmail[],
  ): OutlookEmail[] {
    const resultWithLocalId: OutlookEmail[] = [];

    for (let i = 0; i < maximizerEntries.length; i++) {
      const result: OutlookEmail = { ...maximizerEntries[i] };

      result.localId ??= this.composeService.generateLocalId(
        result,
        result.position,
        i,
      );
      resultWithLocalId.push(result);
    }

    return resultWithLocalId;
  }

  private populateSaveToEntryList(emailsInput: OutlookEmail[]): void {
    const entryList: {
      to: OutlookEmail[];
      cc: OutlookEmail[];
      bcc: OutlookEmail[];
      saveOnly: OutlookEmail[];
    } = { to: [], cc: [], bcc: [], saveOnly: [] };
    emailsInput.forEach((item) => {
      switch (item.position) {
        case 'to':
          entryList.to.push(item);
          break;
        case 'cc':
          entryList.cc.push(item);
          break;
        case 'bcc':
          entryList.bcc.push(item);
          break;
        case 'saveOnly':
          entryList.saveOnly.push(item);
          break;
        default:
          break;
      }
    });

    const currentList = this.baseSaveForEntryList();

    const primarySection = this.getPrimaryRecipientSection(
      entryList.to,
      entryList.cc,
      entryList.bcc,
    );

    const preserveOrSetSelection = (
      email: OutlookEmail,
      sectionItems: (OutlookEmail & { isSelected: boolean })[],
      sectionId: string,
    ) => {
      const existingItem = sectionItems.find(
        (item) => item.localId === email.localId,
      );

      if (existingItem) {
        return {
          ...email,
          isSelected: existingItem.isSelected,
        };
      }

      const allCurrentItems = currentList.flatMap((section) => section.items);
      const existingInAnySection = allCurrentItems.find(
        (item) =>
          item.id === email.id &&
          item.emailAddress === email.emailAddress &&
          item.type === email.type,
      );

      if (existingInAnySection) {
        return {
          ...email,
          isSelected: existingInAnySection.isSelected,
        };
      }

      const defaultSelected = sectionId === primarySection;
      return {
        ...email,
        isSelected: defaultSelected,
      };
    };

    currentList[0].items = entryList.to.map((email) =>
      preserveOrSetSelection(email, currentList[0].items, 'to'),
    );
    currentList[1].items = entryList.cc.map((email) =>
      preserveOrSetSelection(email, currentList[1].items, 'cc'),
    );
    currentList[2].items = entryList.bcc.map((email) =>
      preserveOrSetSelection(email, currentList[2].items, 'bcc'),
    );

    if (entryList.saveOnly.length > 0) {
      for (const saveOnlyItem of entryList.saveOnly) {
        this.composeStore.addEmailToSaveOnly(saveOnlyItem);
      }
    }

    this.baseSaveForEntryList.set([...currentList]);
    this.restoreCheckboxStatesFromSession();
  }

  private async checkIfMultiselectExistsInStorage(): Promise<string[] | null> {
    try {
      const mailboxItemId = await this.outlookService.getComposeEmailId();
      if (!mailboxItemId) return null;

      const json = this.storage.get(
        this.storageName_emailsToSave + ':' + mailboxItemId,
      );
      if (!json) return null;
      const result = JSON.parse(json);
      if (!result) return null;
      return result;
    } catch (error: any) {
      this.insightsService.trackException({ error });
      return null;
    }
  }

  async handleSaveForEntryListChange(emails?: OutlookEmail[]): Promise<void> {
    if (this.outlookStore.hasInvalidComposeProperties()) return;

    let result: OutlookEmail[] = [];
    const allItems = this.saveForEntryList().flatMap((group) => group.items);
    const selectedEmails = allItems.filter((item) => item.isSelected);

    result = selectedEmails.filter(
      (o) => !this.parentItems.includes(o.localId ?? ''),
    );

    if (result.length === 0 && emails && emails.length > 0) {
      const incomingData = emails.filter(
        (o) => !this.parentItems.includes(o.localId ?? ''),
      );

      const savedData = this.composeStore
        .selectedEmailsToSave()
        .filter((o) => !this.parentItems.includes(o.localId ?? ''));

      const removedEmails = savedData.filter(
        (o) => !incomingData.some((p) => p.localId === o.localId),
      );

      if (removedEmails.length > 0) {
        result = this.handleRemovedItems(removedEmails);
      } else {
        result = this.handleNewItems(incomingData, savedData);
      }
    }

    this.composeStore.updateSelectedEmailsToSave([...result]);

    const switchInSession = await this.getSwitchInSession();
    this.saveToEmailSwitch = !this.hasSyncErrors() && (switchInSession ?? true);

    await this.saveSelectedEmailsInSession(result);
    this.heartbeatDebounce$.next();
  }

  private async saveSelectedEmailsInSession(
    result: OutlookEmail[],
  ): Promise<void> {
    try {
      const mailboxItemId = await this.outlookService.getComposeEmailId();
      if (!mailboxItemId) return;

      const uniqueIds = Array.from(
        new Set(result.filter((o) => o.id !== undefined).map((o) => o.id)),
      );

      this.storage.set(
        this.storageName_emailsToSave + ':' + mailboxItemId,
        JSON.stringify(uniqueIds),
      );
    } catch (error: any) {
      this.insightsService.trackException(
        { error },
        { message: 'Failed to store session saveTo emails' },
      );
    }
  }

  private saveCheckboxStatesToSession(): void {
    const checkboxStates: { [localId: string]: boolean } = {};
    const allItems = this.saveForEntryList().flatMap((group) => group.items);

    allItems.forEach((item) => {
      if (item.localId) {
        checkboxStates[item.localId] = item.isSelected;
      }
    });

    this.composeStore.saveCheckboxStateInSession(checkboxStates);
  }

  private restoreCheckboxStatesFromSession(): void {
    const savedStates = this.composeStore.getCheckboxStateFromSession();
    if (!savedStates) {
      return;
    }

    const currentList = [...this.baseSaveForEntryList()];
    let updated = false;

    for (const group of currentList) {
      for (const item of group.items) {
        if (item.localId && savedStates.hasOwnProperty(item.localId)) {
          item.isSelected = savedStates[item.localId];
          updated = true;
        }
      }
    }

    if (updated) {
      this.baseSaveForEntryList.set(currentList);
    }
  }

  private removeCheckboxStateForEmail(email: OutlookEmail): void {
    if (email.localId) {
      this.composeStore.removeCheckboxStateFromSession(email.localId);
    }
  }

  private clearAllCheckboxStates(): void {
    const composeId = this.composeStore.composeId();
    if (composeId) {
      sessionStorage.removeItem(`checkboxStates_${composeId}`);
    }
  }

  private handleRemovedItems(removedEmails: OutlookEmail[]): OutlookEmail[] {
    const result: OutlookEmail[] = [];
    for (const email of this.composeStore.selectedEmailsToSave()) {
      if (removedEmails.some((o) => o.id === email.id)) {
        continue;
      }
      result.push(email);
    }

    return result;
  }

  private handleNewItems(
    incomingData: OutlookEmail[],
    savedData: OutlookEmail[],
  ): OutlookEmail[] {
    let output: OutlookEmail[] = [];
    output = output.concat(this.composeStore.selectedEmailsToSave());
    let emailsToSelect: OutlookEmail[] = [];
    for (const parent of this.saveForEntryList()) {
      emailsToSelect = emailsToSelect.concat(parent.items);
    }
    const addedEmails = incomingData.filter(
      (o) => !savedData.some((p) => p.localId === o.localId),
    );
    for (const email of addedEmails) {
      const allOccurrences = emailsToSelect.filter(
        (o) =>
          o.id === email.id && !output.some((r) => r.localId === o.localId),
      );
      if (allOccurrences.length) {
        output = output.concat(allOccurrences);
      }
    }
    return output.filter((o) => !this.parentItems.includes(o.localId ?? ''));
  }

  countSelectedItemsIgnoringParent(event?: ComposeEntryGroup[]): number {
    const finalItems = event?.filter((e) => e.items === undefined) ?? [];
    return finalItems.length;
  }

  async handleSwitchValueChanged(event: boolean): Promise<void> {
    this.composeStore.setLoading(true);
    try {
      if (this.hasSyncErrors()) {
        this.saveToEmailSwitch = false;
        await this.deleteHeartbeatIfActive();
        this.outlookService.stopHeartbeat();
        return;
      }

      await this.saveSwitchInSession(event);

      if (event && !this.autoSaveDisabled()) {
        await this.sendHeartbeat();
        this.startHeartbeatTimer();
      } else {
        await this.deleteHeartbeatIfActive();
        this.outlookService.stopHeartbeat();
      }
    } catch (error: any) {
      this.insightsService.trackException(
        { error },
        {
          message: 'Failed to handle switch value change',
          event,
          emailsToSave: this.composeStore.selectedEmailsToSave(),
        },
      );
    } finally {
      this.composeStore.setLoading(false);
    }
  }

  private async getSwitchInSession(): Promise<boolean | undefined> {
    try {
      const mailboxItemId = await this.outlookService.getComposeEmailId();
      if (!mailboxItemId) return undefined;
      const json = this.storage.get(
        this.storageName_saveToSwitch + ':' + mailboxItemId,
      );
      if (!json) return undefined;
      return JSON.parse(json);
    } catch (error) {
      this.insightsService.trackException(
        { error: error as Error },
        { message: 'Failed to read session saveTo switch' },
      );
      return undefined;
    }
  }

  private async saveSwitchInSession(saveToSwitch: boolean): Promise<void> {
    try {
      const mailboxItemId = await this.outlookService.getComposeEmailId();
      if (!mailboxItemId) return;

      const key = this.storageName_saveToSwitch + ':' + mailboxItemId;
      if (saveToSwitch) {
        this.storage.remove(key);
      } else {
        this.storage.set(key, JSON.stringify(false));
      }
    } catch (error) {
      console.error('Failed to store session saveTo switch', error);
    }
  }

  async saveNow(): Promise<void> {
    const allItems = this.saveForEntryList().flatMap((group) => group.items);
    const emailsToSave = allItems.filter((item) => item.isSelected);

    if (emailsToSave.length === 0) {
      return;
    }

    if (this.outlookStore.preferences().saveEmailDetail) {
      this.openSaveEmailDetails(emailsToSave);
    } else {
      this.composeStore.setLoading(true);
      try {
        const result = await this.getSavedMailboxEmlFile();
        if (!result) throw new Error('Unable to get mailbox item or EML file');

        await this.saveEmailNowIterator(emailsToSave, result.emlFile);
      } catch (error) {
        this.showSaveEmailErrorNotification(emailsToSave.length);

        if (error instanceof Error) {
          this.insightsService.trackException({ error });
        }
      } finally {
        this.composeStore.setLoading(false);
      }
    }
  }

  private async openSaveEmailDetails(entries: OutlookEmail[]): Promise<void> {
    try {
      const subject = await this.getEmailSubject();

      this.saveEmailDetail = true;
      this.saveEmailDetailProperties = {
        subject,
        entry: entries,
      };
    } catch {
      this.notification().showMessage('error', 'outlook.email.details.error');
    }
  }

  private async getSavedMailboxEmlFile(): Promise<{
    item: Office.MessageRead | Office.MessageCompose;
    emlFile: File;
  } | null> {
    const item = this.outlookService.mailboxItem;
    if (!item) return null;

    const emlFile = await this.outlookService.getSavedComposeEmlFile();
    if (!emlFile) return null;

    return { item, emlFile };
  }

  onSaveEmailDetail(entries: OutlookEmail[]): void {
    this.saveEmailDetail = false;
    this.saveEmailDetailProperties = undefined; // reset
    this.showSaveEmailSuccessNotification(entries.length);

    for (const entry of entries) {
      this.updateLastContactDate(entry);
    }
  }

  private async saveEmailNowIterator(
    toSaveEntries: OutlookEmail[],
    emlFile: File,
  ): Promise<{
    createdEmails: OutlookEmailCreationResult[];
    failedEmails: string[];
  }> {
    const createdEmails: OutlookEmailCreationResult[] = [];
    const failedEmails: string[] = [];

    const validEntries = toSaveEntries.filter(
      (entry): entry is OutlookEmail & { id: string } => !!entry.id,
    );
    const parentKeys = validEntries.map((entry) => entry.id);

    if (parentKeys.length === 0) {
      return { createdEmails, failedEmails };
    }

    try {
      const documentIds = await this.emailService.saveEmailToMultipleEntries(
        emlFile,
        parentKeys,
        true,
      );

      for (let i = 0; i < validEntries.length; i++) {
        const entry = validEntries[i];
        const documentId = documentIds?.[i];

        if (documentId) {
          createdEmails.push({ parentKey: entry.id, documentId });

          this.updateLastContactDate(entry);
          this.trackEmailSaveResult(true, entry);
        } else {
          failedEmails.push(entry.id);
          this.trackEmailSaveResult(false, entry);
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        this.insightsService.trackException({ error });
      }

      for (const entry of validEntries) {
        failedEmails.push(entry.id);
        this.trackEmailSaveResult(false, entry);
      }
    }

    if (createdEmails.length > 0) {
      this.showSaveEmailSuccessNotification(createdEmails.length);
    }

    if (failedEmails.length > 0) {
      this.showSaveEmailErrorNotification(failedEmails.length);
    }

    return { createdEmails, failedEmails };
  }

  private trackEmailSaveResult(success: boolean, entry: OutlookEmail): void {
    let entityType: EntryType | AbEntryType | EmailEntryType;

    if (entry.type === EntryType.AbEntry && entry.abentryType) {
      entityType = entry.abentryType;
    } else {
      entityType = entry.type ?? EntryType.Contact;
    }

    this.tracker.trackEvent({
      module: EventModule.Contact,
      journey: EventJourney.Save,
      action: EventAction.Completed,
      metadata: {
        result: success ? EventMetadata.Success : EventMetadata.Failed,
        entity: entityType,
        emailDetails: false,
        search: false,
      },
    });
  }

  private updateLastContactDate(entry: OutlookEmail): void {
    if (!entry.id) return;
    const currentDate = new Date();

    this.abEntryService
      .updateLastContactDateWithPreferences(
        entry.id,
        EntryType.AbEntry,
        AbEntryLastContactDatePreference.EmailSentFromOutlook,
        currentDate,
        entry.abentryType,
        entry.parentKey,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  private showSaveEmailSuccessNotification(savedEntries: number): void {
    const successMsg = this.translate.instant(
      'outlook.emailNotification.successSaveToAmount',
      { count: savedEntries },
    );
    this.notification().showMessage('success', successMsg);
  }

  private showSaveEmailErrorNotification(failedEntries: number): void {
    const translationEntry = 'outlook.emailNotification.failedSaveToAmount';
    const errorMsg = this.translate.instant(translationEntry, {
      count: failedEntries,
    });
    this.notification().showMessage('error', errorMsg);
  }

  private async getEmailSubject(): Promise<string> {
    const item = this.outlookService
      .mailboxItem as Office.MessageCompose | null;

    const subject = item?.subject?.getAsync
      ? await tryOfficeAsync<string>(item.subject.getAsync, false)
      : null;

    return (
      nullIfBlank(subject) ?? this.translate.instant('outlook.email.noSubject')
    );
  }
}
